import { parseAsArrayOf, parseAsString, useQueryStates } from 'nuqs'

// Define all filter parsers
const shipmentNotificationFilterParsers = {
  // Search query
  search: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),

  // Date filters
  shouldArrivedAtFrom: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),
  shouldArrivedAtTo: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),

  // Status filters - Por defecto solo CREATED y PENDING_TO_ARRIVE para optimizar conexión
  status: parseAsArrayOf(parseAsString, ',')
    .withDefault(['CREATED', 'PENDING_TO_ARRIVE'])
    .withOptions({
      clearOnDefault: true,
    }),

  // Company filters
  providerCompanyId: parseAsArrayOf(parseAsString, ',')
    .withDefault(['all'])
    .withOptions({
      clearOnDefault: true,
    }),
  managerCompanyId: parseAsArrayOf(parseAsString, ',')
    .withDefault(['all'])
    .withOptions({
      clearOnDefault: true,
    }),

  // Created by filter
  createdById: parseAsArrayOf(parseAsString, ',')
    .withDefault(['all'])
    .withOptions({
      clearOnDefault: true,
    }),
}

export function useShipmentNotificationFilters() {
  const [filters, setFilters] = useQueryStates(
    shipmentNotificationFilterParsers,
    {
      history: 'replace', // Use replace to avoid cluttering browser history
      shallow: true, // Client-side only updates
    }
  )

  // Helper functions for easier state management
  const updateSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search }))
  }

  const updateShouldArrivedAtFrom = (shouldArrivedAtFrom: string) => {
    setFilters((prev) => ({ ...prev, shouldArrivedAtFrom }))
  }

  const updateShouldArrivedAtTo = (shouldArrivedAtTo: string) => {
    setFilters((prev) => ({ ...prev, shouldArrivedAtTo }))
  }

  const updateStatus = (statuses: string[]) => {
    setFilters((prev) => ({ ...prev, status: statuses }))
  }

  const updateProviderCompanyId = (providerCompanyIds: string[]) => {
    setFilters((prev) => ({ ...prev, providerCompanyId: providerCompanyIds }))
  }

  const updateManagerCompanyId = (managerCompanyIds: string[]) => {
    setFilters((prev) => ({ ...prev, managerCompanyId: managerCompanyIds }))
  }

  const updateCreatedById = (createdByIds: string[]) => {
    setFilters((prev) => ({ ...prev, createdById: createdByIds }))
  }

  const clearAllFilters = () => {
    // Clear all filters in a single atomic operation to prevent step-by-step clearing
    setFilters({
      search: '',
      shouldArrivedAtFrom: '',
      shouldArrivedAtTo: '',
      status: ['CREATED', 'PENDING_TO_ARRIVE'], // Volver a filtros optimizados por defecto
      providerCompanyId: ['all'],
      managerCompanyId: ['all'],
      createdById: ['all'],
    })
  }

  // Check if any filters are active (excluding defaults and column-only filters)
  const hasActiveFilters =
    filters.search !== '' ||
    filters.shouldArrivedAtFrom !== '' ||
    filters.shouldArrivedAtTo !== '' ||
    (!filters.status.includes('all') &&
      (filters.status.length !== 2 ||
        !filters.status.includes('CREATED') ||
        !filters.status.includes('PENDING_TO_ARRIVE'))) ||
    !filters.providerCompanyId.includes('all') ||
    !filters.managerCompanyId.includes('all') ||
    !filters.createdById.includes('all')

  return {
    // Current filter values
    filters,

    // Update functions
    updateSearch,
    updateShouldArrivedAtFrom,
    updateShouldArrivedAtTo,
    updateStatus,
    updateProviderCompanyId,
    updateManagerCompanyId,
    updateCreatedById,
    setFilters,

    // Clear functions
    clearAllFilters,

    // Status helpers
    hasActiveFilters,
  }
}
